/**
 * URL Validation and Sanitization Utilities
 *
 * Provides security functions to validate and sanitize redirect URLs,
 * preventing open redirect vulnerabilities and protocol-based attacks.
 */

const ALLOWED_WEB_PROTOCOLS = ['https:', 'http:'];

/**
 * List of known safe deep link schemes used in the application.
 * These are mobile app protocols that are legitimate redirect targets.
 */
const ALLOWED_DEEPLINK_SCHEMES = ['phonepe:', 'paytm:', 'gpay:', 'upi:', 'tez:', 'bhim:', 'binancepay:', 'intent:'];

interface SafeRedirectOptions {
  allowDeepLinks?: boolean;
}

/**
 * Checks if a URL uses a safe protocol for redirects.
 *
 * @param url - The URL to validate
 * @param options - Configuration options
 * @param options.allowDeepLinks - Whether to allow known deep link schemes (default: false)
 * @returns true if the URL is safe for redirect, false otherwise
 *
 * @example
 * isSafeRedirectUrl('https://example.com') // true
 * isSafeRedirectUrl('/path') // true (relative URL)
 * isSafeRedirectUrl('javascript:alert(1)') // false
 * isSafeRedirectUrl('phonepe://pay', { allowDeepLinks: true }) // true
 */
export const isSafeRedirectUrl = (url: string, options: SafeRedirectOptions = {}): boolean => {
  const { allowDeepLinks = false } = options;

  if (!url || url === '/') {
    return true;
  }

  // Relative URLs starting with / are safe
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    // Handle protocol-relative URLs (//example.com)
    const normalizedUrl = url.startsWith('//') ? `${window.location.protocol}${url}` : url;

    const parsed = new URL(normalizedUrl, window.location.origin);

    if (ALLOWED_WEB_PROTOCOLS.includes(parsed.protocol)) {
      return true;
    }

    if (allowDeepLinks) {
      const scheme = parsed.protocol.toLowerCase();
      return ALLOWED_DEEPLINK_SCHEMES.includes(scheme);
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Sanitizes a redirect URL, returning the fallback if the URL is unsafe.
 *
 * @param url - The URL to sanitize
 * @param fallback - The fallback URL to use if the input is unsafe (default: '/')
 * @param options - Configuration options
 * @param options.allowDeepLinks - Whether to allow known deep link schemes (default: false)
 * @returns The sanitized URL or the fallback
 *
 * @example
 * sanitizeRedirectUrl('https://example.com') // 'https://example.com'
 * sanitizeRedirectUrl('javascript:alert(1)') // '/'
 * sanitizeRedirectUrl('//example.com') // 'https://example.com' (normalized)
 * sanitizeRedirectUrl('phonepe://pay', '/', { allowDeepLinks: true }) // 'phonepe://pay'
 */
export const sanitizeRedirectUrl = (url: string, fallback = '/', options: SafeRedirectOptions = {}): string => {
  const { allowDeepLinks = false } = options;

  if (!url) {
    return fallback;
  }

  if (url === '/') {
    return url;
  }

  // Relative URLs starting with / are safe
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url;
  }

  // Normalize protocol-relative URLs
  const normalizedUrl = url.startsWith('//') ? `${window.location.protocol}${url}` : url;

  try {
    const parsed = new URL(normalizedUrl, window.location.origin);

    if (ALLOWED_WEB_PROTOCOLS.includes(parsed.protocol)) {
      return parsed.href;
    }

    if (allowDeepLinks) {
      const scheme = parsed.protocol.toLowerCase();
      if (ALLOWED_DEEPLINK_SCHEMES.includes(scheme)) {
        return url; // Return original URL for deep links
      }
    }

    return fallback;
  } catch {
    return fallback;
  }
};

/**
 * Opens a URL in a new window/tab with security attributes to prevent reverse tabnapping.
 * Uses noopener and noreferrer to prevent the opened page from accessing window.opener.
 *
 * @param url - The URL to open
 * @param fallback - The fallback URL if the input is unsafe (default: '/')
 * @returns The window reference or null
 */
export const safeWindowOpen = (url: string, fallback = '/'): Window | null => {
  const safeUrl = sanitizeRedirectUrl(url, fallback);
  return window.open(safeUrl, '_blank', 'noopener,noreferrer');
};

/**
 * Safely assigns a URL to window.location after sanitization.
 *
 * @param url - The URL to navigate to
 * @param fallback - The fallback URL if the input is unsafe (default: '/')
 * @param options - Configuration options
 * @param options.allowDeepLinks - Whether to allow known deep link schemes (default: false)
 */
export const safeLocationAssign = (url: string, fallback = '/', options: SafeRedirectOptions = {}): void => {
  const safeUrl = sanitizeRedirectUrl(url, fallback, options);
  window.location.assign(safeUrl);
};

/**
 * Safely sets window.location.href after sanitization.
 *
 * @param url - The URL to navigate to
 * @param fallback - The fallback URL if the input is unsafe (default: '/')
 * @param options - Configuration options
 * @param options.allowDeepLinks - Whether to allow known deep link schemes (default: false)
 */
export const safeLocationHref = (url: string, fallback = '/', options: SafeRedirectOptions = {}): void => {
  const safeUrl = sanitizeRedirectUrl(url, fallback, options);
  window.location.href = safeUrl;
};
